/**
 * DistributionBar — one labelled proportion row (option → share of respondents).
 *
 * A lightweight, theme-tokened horizontal bar used for survey response distributions
 * (likert/single/multi option splits, NPS buckets). Not a chart library — just a
 * flex row with a proportional fill, which reads cleanly stacked many-per-question.
 */
import React, { FC } from "react";
import { Box, Typography } from "@mui/material";

type DistributionBarProps = {
    label: string;
    count: number;
    /** Share of respondents (0–1) — drives the fill width. */
    pct: number;
    /** Fill colour; defaults to the primary teal. */
    color?: string;
};

export const DistributionBar: FC<DistributionBarProps> = ({ label, count, pct, color }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
        <Typography variant="body2" sx={{ width: "40%", flexShrink: 0 }} noWrap title={label}>
            {label}
        </Typography>
        <Box
            sx={{
                position: "relative",
                flex: 1,
                height: 20,
                borderRadius: 1,
                bgcolor: "action.hover",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: `${Math.round(pct * 100)}%`,
                    bgcolor: color ?? "primary.main",
                    borderRadius: 1,
                    transition: "width 300ms ease",
                }}
            />
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ width: 72, flexShrink: 0, textAlign: "right" }}>
            {count} · {Math.round(pct * 100)}%
        </Typography>
    </Box>
);
